import { useEffect, useState, type PropsWithChildren } from "react";

import { createContext } from "@radix-ui/react-context";
import * as Popover from "@radix-ui/react-popover";

import { Tooltip } from "..";
import { cn } from "../index";

/* -------------------------------------------------------------------------------------------------
 * TouchProvider
 * -----------------------------------------------------------------------------------------------*/

interface HibridTooltipContextValue {
  isTouch: boolean;
}

const [HybridTooltipProvider, useHybridTooltipContext] = createContext<HibridTooltipContextValue>("HybridTooltip");

const TouchProvider = ({ children }: PropsWithChildren) => {
  const [isTouch, setTouch] = useState(false);

  useEffect(() => {
    const mq = window.matchMedia("(pointer: coarse)");
    const update = () => setTouch(mq.matches);
    update();
    mq.addEventListener("change", update);
    return () => mq.removeEventListener("change", update);
  }, []);

  return <HybridTooltipProvider isTouch={isTouch}>{children}</HybridTooltipProvider>;
};

TouchProvider.displayName = "TouchProvider";

/* -------------------------------------------------------------------------------------------------
 * HybridTooltip
 * -----------------------------------------------------------------------------------------------*/

const HYBRID_TOOLTIP_NAME = "HybridTooltip";

type HybridTooltipProps = Tooltip.TooltipProps & Popover.PopoverProps;

const Root: React.FC<HybridTooltipProps> = props => {
  const { isTouch } = useHybridTooltipContext(HYBRID_TOOLTIP_NAME);

  return isTouch ? <Popover.Root {...props} /> : <Tooltip.Root {...props} />;
};

Root.displayName = "HybridTooltip";

/* -------------------------------------------------------------------------------------------------
 * HybridTooltipTrigger
 * -----------------------------------------------------------------------------------------------*/

type TriggerProps = Tooltip.TriggerProps & Popover.PopoverTriggerProps;

const Trigger: React.FC<TriggerProps> = props => {
  const { isTouch } = useHybridTooltipContext(HYBRID_TOOLTIP_NAME);

  return isTouch ? <Popover.Trigger {...props} /> : <Tooltip.Trigger {...props} />;
};

Trigger.displayName = "HybridTooltipTrigger";

/* -------------------------------------------------------------------------------------------------
 * HybridTooltipPortal
 * -----------------------------------------------------------------------------------------------*/

type PortalProps = Tooltip.PortalProps & Popover.PopoverPortalProps;

const Portal: React.FC<PortalProps> = props => {
  const { isTouch } = useHybridTooltipContext(HYBRID_TOOLTIP_NAME);

  return isTouch ? <Popover.Portal {...props} /> : <Tooltip.Portal {...props} />;
};

Portal.displayName = "HybridTooltipPortal";

/* -------------------------------------------------------------------------------------------------
 * HybridTooltipContent
 * -----------------------------------------------------------------------------------------------*/

type ContentProps = Tooltip.ContentProps & Popover.PopoverContentProps;

const Content: React.FC<ContentProps> = ({ className, ...props }) => {
  const { isTouch } = useHybridTooltipContext(HYBRID_TOOLTIP_NAME);

  return isTouch ? (
    <Popover.Content
      className={cn(
        "p-5 rounded-[12px] bg-primary-dark-blue text-base whitespace-pre-line text-primary-white outline-none",
        className,
      )}
      {...props}
    />
  ) : (
    <Tooltip.Content className={className} {...props} />
  );
};

Content.displayName = "HybridTooltipContent";

/* -------------------------------------------------------------------------------------------------
 * HybridTooltipArrow
 * -----------------------------------------------------------------------------------------------*/

type ArrowProps = Tooltip.ArrowProps & Popover.PopoverArrowProps;

const Arrow: React.FC<ArrowProps> = ({ className, ...props }) => {
  const { isTouch } = useHybridTooltipContext(HYBRID_TOOLTIP_NAME);

  return isTouch ? (
    <Popover.Arrow className={cn("fill-primary-dark-blue", className)} {...props} />
  ) : (
    <Tooltip.Arrow className={className} {...props} />
  );
};

Arrow.displayName = "HybridTooltipArrow";

export { TouchProvider, Root, Trigger, Portal, Content, Arrow, useHybridTooltipContext };
export type { HybridTooltipProps, TriggerProps, PortalProps, ContentProps, ArrowProps };
